import { z } from 'zod'

/**
 * Wire contracts shared by every API call.
 *
 * Contract policy (see `src/docs/Guardrails.md`):
 * - Request payloads use `.strict()` so unknown keys are rejected instead of
 *   smuggled through to a service.
 * - Response schemas are **lenient** (zod's default strips unknown keys) so a
 *   service may add fields without breaking deployed clients. Contract drift is
 *   still caught: missing or wrongly-typed fields fail loudly at runtime.
 */

/** Error body returned by the BFF for non-2xx responses. */
export const apiErrorSchema = z.object({
  code: z.string().min(1),
  message: z.string().min(1),
  retryable: z.boolean().optional(),
})

export type ApiErrorBody = z.infer<typeof apiErrorSchema>

/** Cursor-pagination metadata (AGENTS.md: keyset pagination everywhere). */
export const paginationMetaSchema = z.object({
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
  totalCount: z.number().int().nonnegative(),
})

/** Standard list envelope: `{ data, meta }`. */
export function apiEnvelopeSchema<T extends z.ZodTypeAny>(data: T) {
  return z.object({ data, meta: paginationMetaSchema.optional() })
}

/** Tenant identity as exposed to the client. */
export const tenantSchema = z.object({
  id: z.string().min(1),
  publicId: z.string().min(1).optional(),
  name: z.string().min(1),
})

/** Tenant configuration payload returned to the SPA. */
export const tenantConfigSchema = z.object({
  featureFlags: z.record(z.string(), z.boolean()).default({}),
  branding: z
    .object({
      logoUrl: z.string().optional(),
      primaryColor: z.string().optional(),
      name: z.string().optional(),
    })
    .optional(),
  locale: z.string().min(2),
  timezone: z.string().min(1),
  currency: z.string().length(3),
})

/** Auth token bundle — validated before anything is persisted. */
export const authTokensSchema = z.object({
  accessToken: z.string().min(1),
  refreshToken: z.string().min(1),
  tenantId: z.string().min(1),
})

export type AuthTokensContract = z.infer<typeof authTokensSchema>
